import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../../context/ThemeContext'
import { profile, sections } from '../../data/portfolio'
import { goToSection } from '../../utils/scroll'
import './CommandPalette.scss'

export const OPEN_PALETTE_EVENT = 'open-command-palette'

const sectionIcons = {
  home: 'fa-house',
  capabilities: 'fa-layer-group',
  experience: 'fa-briefcase',
  lab: 'fa-flask',
  work: 'fa-code',
  github: 'fa-code-branch',
  sponsor: 'fa-hand-holding-heart',
  ask: 'fa-robot',
  contact: 'fa-paper-plane'
}

const CommandPalette = () => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [toast, setToast] = useState('')
  const inputRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { isDarkMode, toggleTheme } = useTheme()

  const commands = useMemo(() => [
    ...sections.map((section) => ({
      group: 'Navigate',
      label: `Go to ${section.label}`,
      icon: sectionIcons[section.id],
      run: () => goToSection(section.id, location, navigate)
    })),
    { group: 'Actions', label: 'Copy email address', icon: 'fa-copy', run: () => { navigator.clipboard?.writeText(profile.email); setToast('Email copied to clipboard') } },
    { group: 'Actions', label: 'Open CV', icon: 'fa-id-card', run: () => navigate(profile.cvPage) },
    { group: 'Actions', label: 'Download CV (PDF)', icon: 'fa-file-arrow-down', run: () => window.open(profile.resume, '_blank', 'noopener') },
    { group: 'Actions', label: `Switch to ${isDarkMode ? 'light' : 'dark'} theme`, icon: isDarkMode ? 'fa-sun' : 'fa-moon', run: toggleTheme },
    { group: 'Links', label: 'Open GitHub', icon: 'fa-brands fa-github', run: () => window.open(profile.github, '_blank', 'noopener') },
    { group: 'Links', label: 'Open LinkedIn', icon: 'fa-brands fa-linkedin-in', run: () => window.open(profile.linkedin, '_blank', 'noopener') },
    { group: 'Links', label: 'Message on WhatsApp', icon: 'fa-brands fa-whatsapp', run: () => window.open(profile.whatsapp, '_blank', 'noopener') },
    { group: 'Links', label: 'View services', icon: 'fa-briefcase', run: () => navigate('/services') }
  ], [isDarkMode, location, navigate, toggleTheme])

  const results = commands.filter((command) => command.label.toLowerCase().includes(query.trim().toLowerCase()))

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((current) => !current)
      }
    }
    const onOpen = () => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen)
    }
  }, [])

  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(''), 2200)
    return () => clearTimeout(timer)
  }, [toast])

  const execute = (command) => {
    setOpen(false)
    command.run()
  }

  const onKeyDown = (event) => {
    if (event.key === 'Escape') setOpen(false)
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)) }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)) }
    if (event.key === 'Enter' && results[active]) execute(results[active])
  }

  let lastGroup = ''

  return (
    <>
      {open && (
        <div className="cmdk-overlay" onMouseDown={() => setOpen(false)}>
          <div className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={(event) => event.stopPropagation()} onKeyDown={onKeyDown}>
            <div className="cmdk-search">
              <i className="fas fa-magnifying-glass" />
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => { setQuery(event.target.value); setActive(0) }}
                placeholder="Type a command or search…"
                aria-label="Search commands"
              />
              <kbd>esc</kbd>
            </div>
            <ul className="cmdk-list" role="listbox">
              {results.length === 0 && <li className="cmdk-empty">No matching commands</li>}
              {results.map((command, index) => {
                const showGroup = command.group !== lastGroup
                lastGroup = command.group
                return (
                  <React.Fragment key={command.label}>
                    {showGroup && <li className="cmdk-group">{command.group}</li>}
                    <li
                      role="option"
                      aria-selected={index === active}
                      className={`cmdk-item ${index === active ? 'is-active' : ''}`}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => execute(command)}
                    >
                      <i className={command.icon.startsWith('fa-brands') ? command.icon : `fas ${command.icon}`} />
                      <span>{command.label}</span>
                      <i className="fas fa-arrow-turn-down cmdk-enter" />
                    </li>
                  </React.Fragment>
                )
              })}
            </ul>
            <div className="cmdk-footer"><span><kbd>↑</kbd><kbd>↓</kbd> navigate</span><span><kbd>↵</kbd> select</span><span><kbd>⌘</kbd><kbd>K</kbd> toggle</span></div>
          </div>
        </div>
      )}
      {toast && <div className="app-toast" role="status"><i className="fas fa-circle-check" /> {toast}</div>}
    </>
  )
}

export default CommandPalette
